import { useNavigate } from "react-router-dom";
import { Clock, FileText, Tag } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { CompanyTest } from "@/hooks/useCompanyTests";

interface CompanyTestCardProps {
  test: CompanyTest;
  accentColor: string;
}

export function CompanyTestCard({ test, accentColor }: CompanyTestCardProps) {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/innoventure-test/${test.id}`);
  };

  const getTimeLimit = () => {
    if (test.total_question_time_seconds && test.total_question_time_seconds > 0) {
      const totalMinutes = Math.ceil(test.total_question_time_seconds / 60);
      return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''} total`;
    } else if (test.time_limit_type === 'total' && test.total_time_limit) {
      return `${test.total_time_limit} minute${test.total_time_limit !== 1 ? 's' : ''} total`;
    }
    return 'No time limit';
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/50 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-muted/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="relative pb-4">
        <div className="flex items-start justify-between mb-3">
          <CardTitle className="text-xl font-bold leading-tight pr-2">{test.title}</CardTitle>
          <Badge 
            variant={test.testType === 'invited' ? 'default' : 'secondary'}
            className="shrink-0"
            style={test.testType === 'invited' ? { backgroundColor: accentColor } : undefined}
          >
            {test.testType === 'invited' ? 'Invitation Only' : 'Public'}
          </Badge>
        </div>
        {test.job_title && (
          <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground/90 mb-2">
            <Tag className="h-3.5 w-3.5" />
            <span>{test.job_title}</span>
          </div>
        )}
        <CardDescription className="line-clamp-2 text-base leading-relaxed">
          <div dangerouslySetInnerHTML={{ __html: test.description }} />
        </CardDescription>
      </CardHeader>
      
      <CardContent className="relative pt-2">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-5 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-muted/50">
                <FileText className="h-4 w-4" />
              </div>
              <span className="font-medium">{test.questionCount} questions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-muted/50">
                <Clock className="h-4 w-4" />
              </div>
              <span className="font-medium">{getTimeLimit()}</span>
            </div>
          </div>
          
          <Button 
            onClick={handleViewDetails} 
            className="w-full font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            size="lg"
            style={{ backgroundColor: accentColor }}
          >
            Read Details & Instructions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
